import React from "react";
import { Box, Paper, Typography, TextField, Button, Chip, Alert } from "@mui/material";
import globals from "../../Globals";
import { PageContainer, SectionHeader, DataTable } from "../../Layout/Common";
import { FIXED_TABLE } from "../Localization/loc";
import { friends, push, canEdit, canView, titleHas, date, PlayerLink, ConfirmAction } from "./community";

// Community → Player (service `friends`; Docs/Social.md §5, §8, §10): one account's social state - `friends list <player>` (friends,
// requests both ways), `friends blocks <player>`, and where the title has push, `push devices <player>` + `push settings <player>`.
// The operator's verb: `friends remove <player> <other>` (both rows, every slot but the blocks - those are the player's). `#players`
// views the lists, `players` removes; `#push` views the devices. /<game>/community/player/<link|id> opens a player.
export default class PlayerTab extends React.Component {
    state = { player: "", list: null, blocks: null, devices: null, settings: null, errors: {}, busy: false, action: null };

    componentDidMount() {
        const pending = globals.route?.deep?.[0];
        if (pending) this.setState({ player: pending }, this.lookup);
    }

    lookup = () => {
        const p = this.state.player.trim();
        if (!p) return;
        this.setState({ list: null, blocks: null, devices: null, settings: null, errors: {} });
        // each section on its own: one refused or missing group never hides the others
        // (a backend refusal - "Player not found." - rejects like a transport error: its text is the section's notice)
        const section = (key, call, description, parts) => call(description, parts, { silent: true })
            .then(r => this.setState({ [key]: r && typeof r === "object" ? r : null }))
            .catch(e => this.setState(s => ({ errors: { ...s.errors, [key]: e?.message ?? "failed" } })));
        section("list", friends, "Loading the friends", ["list", p]);
        section("blocks", friends, "Loading the blocks", ["blocks", p]);
        if (titleHas("push") && canView("push")) {
            section("devices", push, "Loading the devices", ["devices", p]);
            section("settings", push, "Loading the push settings", ["settings", p]);
        }
    }

    remove = (row, what) => {
        const me = this.state.list;
        this.setState({ action: {
            title: `Remove ${row.name || row.accountId} from ${me.name || me.playerId}'s ${what}?`, confirmText: "Remove",
            text: "Both accounts lose each other from friends, requests and sent (one write per row, the friends version bumped on both). Blocks stay.",
            run: () => {
                this.setState({ busy: true });
                return friends("Removing", ["remove", me.playerId, row.accountId], { successMessage: "Removed" })
                    .then(() => { this.setState({ action: null }); this.lookup(); })
                    .catch(() => {})
                    .finally(() => this.setState({ busy: false }));
            },
        }});
    }

    table(rows, what, withRemove, atLabel) {
        if (!rows || rows.length === 0) return <Typography variant="body2" color="text.secondary">None.</Typography>;
        const edit = canEdit("players");
        const columns = [
            { id: "name", label: "Account", width: "32%", render: r => <PlayerLink id={r.accountId} name={r.name ?? "(no row)"} /> },
            { id: "seen", label: "Seen", width: "24%", render: r => <span>{r.online ? <Chip size="small" color="success" label="online" sx={{ mr: 1 }} /> : null}{date(r.lastSeen)}{r.banned ? <Chip size="small" color="error" label="banned" sx={{ ml: 1 }} /> : null}</span> },
            { id: "at", label: atLabel, width: "24%", render: r => date(r.at) },
            { id: "actions", label: "", width: "20%", align: "right", render: r => withRemove && edit ? <Button size="small" color="error" onClick={() => this.remove(r, what)} disabled={this.state.busy}>Remove</Button> : "" },
        ];
        return <DataTable sx={FIXED_TABLE} columns={columns} rows={rows} getRowKey={r => r.accountId} />;
    }

    render() {
        const { player, list, blocks, devices, settings, errors, busy, action } = this.state;
        const err = (key) => errors[key] ? <Alert severity="warning" sx={{ mt: 1 }}>{errors[key]}</Alert> : null;
        const deviceColumns = [
            { id: "token", label: "Token", width: "30%", render: d => <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{d.token} ({d.tokenLength})</span> },
            { id: "platform", label: "Platform", width: "12%" },
            { id: "language", label: "Lang", width: "8%" },
            { id: "branch", label: "Branch", width: "14%" },
            { id: "at", label: "Registered", width: "18%", render: d => date(d.at) },
            { id: "failedAt", label: "Failed", width: "18%", render: d => d.failedAt ? date(d.failedAt) : "—" },
        ];
        return (
            <PageContainer>
                <SectionHeader title="Player">
                    <Typography variant="body2" color="text.secondary">{globals.selectedTitle} · friends, blocks and push of one account</Typography>
                </SectionHeader>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <TextField label="Player (link or id)" size="small" value={player} onChange={e => this.setState({ player: e.target.value })} onKeyDown={e => { if (e.key === "Enter") this.lookup(); }} sx={{ width: 300 }} />
                        <Button variant="outlined" onClick={this.lookup} disabled={busy || !player.trim()}>Look up</Button>
                        {list && <Typography variant="body2"><b>{list.name}</b> · <PlayerLink id={list.playerId} /> · {list.branch} · friends v{list.version}{list.stamped === false ? " (no friends document yet)" : ""}</Typography>}
                    </Box>
                    {err("list")}
                </Paper>
                {list && (
                    <>
                        <Paper sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Friends ({(list.friends ?? []).length}/{list.caps?.maxFriends ?? "?"})</Typography>
                            {this.table(list.friends, "friends", true, "Since")}
                        </Paper>
                        <Paper sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Requests received ({(list.requests ?? []).length}/{list.caps?.maxRequests ?? "?"})</Typography>
                            {this.table(list.requests, "requests", true, "Asked")}
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Requests sent ({(list.sent ?? []).length}/{list.caps?.maxRequests ?? "?"})</Typography>
                            {this.table(list.sent, "sent requests", true, "Asked")}
                        </Paper>
                    </>
                )}
                {(blocks || errors.blocks) && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Blocked by this player ({(blocks?.blocked ?? []).length}/{blocks?.maxBlocked ?? "?"})</Typography>
                        {blocks && this.table(blocks.blocked, "blocks", false, "Blocked")}
                        {err("blocks")}
                    </Paper>
                )}
                {(devices || settings || errors.devices || errors.settings) && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Push devices {devices ? `(${(devices.devices ?? []).length}) · FCM ${devices.fcm}` : ""}</Typography>
                        {devices && ((devices.devices ?? []).length > 0
                            ? <DataTable sx={FIXED_TABLE} columns={deviceColumns} rows={devices.devices} getRowKey={(d, i) => `${d.token}-${i}`} />
                            : <Typography variant="body2" color="text.secondary">No device registered.</Typography>)}
                        {err("devices")}
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Notification settings</Typography>
                        {settings && (
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                {(settings.settings ?? []).map(s => (
                                    <Chip key={s.kind} size="small" color={s.on ? "success" : "default"} variant={s.on === s.byDefault ? "outlined" : "filled"}
                                          title={`kind ${s.kind}; default ${s.byDefault ? "on" : "off"}`} label={`${s.name} ${s.on ? "on" : "off"}`} />
                                ))}
                            </Box>
                        )}
                        {err("settings")}
                    </Paper>
                )}
                <ConfirmAction action={action} busy={busy} onClose={() => this.setState({ action: null })} />
            </PageContainer>
        );
    }
}
